import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { connectAsync } from 'iguazu';
import { externalRedirect } from '@americanexpress/one-app-ducks';
import { queryResource } from 'iguazu-rest';
import { queryModuleWithData } from 'iguazu-holocron';
import { createStructuredSelector } from 'reselect';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { isUS } from '@americanexpress/axp-manage-bookings-utils/build/utilities/helpers';
import { getAkamaiDomain } from '../selectors/selectors';
import Spinner from './Spinner/Spinner';
import Footer from './Footer';
import { getLogoutDestPage, generateModuleConfig } from '../helpers/helpers';
import styles from './ManageBookingsContainer.scss';
import {
  EN_HK, ZH_HK, EN_IN,
} from '../helpers/constant';

const getTravelerLocale = (travelerData, activeLocale) => travelerData?.card?.locale
  || activeLocale;

const isLoadedWithErrors = (loadedWithErrors, loadErrors) => loadedWithErrors()
  && loadErrors.travelerData === false;

function ManageBookingsContainer({
  isLoading, loadedWithErrors, children, langPack,
  activeLocale, akamaiDomain, GlobalHeader, SiteAreaNav, GlobalFooter,
  Help, travelerData, loadErrors,
}) {
  React.useEffect(() => {
    const travelerLocale = getTravelerLocale(travelerData, activeLocale);
    if (travelerData) {
      if (travelerLocale?.toLowerCase() === EN_IN) {
        externalRedirect(`${akamaiDomain}/travel`);
      }
      if (travelerLocale?.toLowerCase() === ZH_HK && activeLocale?.toLowerCase() !== EN_HK) {
        externalRedirect(`${akamaiDomain}/en-hk/travel/my-bookings/summary`);
      } else if (travelerLocale?.toLowerCase() !== ZH_HK && (activeLocale !== travelerLocale)) {
        externalRedirect(`${akamaiDomain}/${travelerLocale?.toLowerCase()}/travel/my-bookings/summary`);
      }
    }
  }, [activeLocale, travelerData, akamaiDomain]);

  const moduleConfig = React.useMemo(() => generateModuleConfig(activeLocale, langPack),
    [activeLocale]);

  if (isLoading() || Object.keys(langPack).length === 0) {
    return <Spinner label="Container Progress Bar" />;
  }

  if (isLoadedWithErrors(loadedWithErrors, loadErrors)) {
    if (process.env.NODE_ENV === 'development') {
      return <div>ManageBookingsContainer failed to load some of its asynchronous data</div>;
    }

    return null;
  }

  return (
    <div data-module-name="axp-manage-bookings-container" className="body">
      <div className={isUS(activeLocale) ? styles.globalHeader : ''}>
        {GlobalHeader && (
          <GlobalHeader
            logoutDestPage={getLogoutDestPage(akamaiDomain, activeLocale)}
          />
        )}
      </div>
      {SiteAreaNav && <SiteAreaNav />}
      <main className={`container ${styles.subnavSpace}`}>
        {children && React.cloneElement(children, {
          moduleConfig,
        })}
        {Help && <Help />}
      </main>
      <Footer locale={langPack?.locale?.toLowerCase()} langPack={langPack} />
      {GlobalFooter && <GlobalFooter />}
    </div>
  );
}

ManageBookingsContainer.propTypes = {
  isLoading: PropTypes.func.isRequired,
  loadedWithErrors: PropTypes.func.isRequired,
  loadErrors: PropTypes.shape({
    travelerData: PropTypes.bool.isRequired,
  }),
  children: PropTypes.node.isRequired,
  langPack: PropTypes.shape({
    locale: PropTypes.string,
  }).isRequired,
  travelerData: PropTypes.shape({}),
  Help: PropTypes.func.isRequired,
  activeLocale: PropTypes.string.isRequired,
  akamaiDomain: PropTypes.string.isRequired,
  GlobalHeader: PropTypes.node.isRequired,
  SiteAreaNav: PropTypes.node.isRequired,
  GlobalFooter: PropTypes.node.isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      id: PropTypes.string,
    }),
    query: PropTypes.shape({
      tripId: PropTypes.string,
    }),
  }).isRequired,
};

const mapStateToProps = createStructuredSelector({
  activeLocale: getActiveLocaleSelector,
  akamaiDomain: getAkamaiDomain,
});

export function loadDataAsProps({ store: { dispatch }, ownProps }) {
  const modules = {
    GlobalHeader: () => dispatch(queryModuleWithData('axp-global-header')),
    SiteAreaNav: () => dispatch(queryModuleWithData('axp-travel-navigation')),
    GlobalFooter: () => dispatch(queryModuleWithData('axp-footer')),
    Help: () => dispatch(queryModuleWithData('axp-manage-bookings-help')),
    travelerData: () => dispatch(queryResource({ resource: 'traveler', id: { locale: ownProps.activeLocale } })),
  };
  return modules;
}

const hocChain = compose(
  connect(mapStateToProps),
  connectAsync({ loadDataAsProps })
);

export { ManageBookingsContainer as TestManageBookingsContainer };

export default hocChain(ManageBookingsContainer);
